import type {CSSProperties} from 'react';
import {font, radius, weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeBlipPlanLook = 'blip-plan-pill' | 'blip-plan-primary' | 'blip-plan-danger' | 'blip-plan-bulk' | 'blip-plan-chip';

const BLIP_PLAN_LOOKS = new Set<string>(['blip-plan-pill', 'blip-plan-primary', 'blip-plan-danger', 'blip-plan-bulk', 'blip-plan-chip']);
export const isWedgeBlipPlanLook = (look: string): look is WedgeBlipPlanLook => BLIP_PLAN_LOOKS.has(look);

/* Released Blip action plan card and quick actions (Wedge). The pill is the shared pill of the prototype (`pillBtn`): 8px by 16px of padding, a
   10px radius, a 0.5px separator border, a transparent fill, the primary ink and 13px bold type. `blip-plan-primary` drops the border and fills
   with the accent under the on-accent ink; `blip-plan-danger` fills with the red instead. `blip-plan-bulk` is the All and None pill: the pill
   with 3px by 9px of padding and 11.5px type. `blip-plan-chip` is the amber quick action chip: an inline row of a glyph and a label with a 6px
   gap, 32px tall at least, 7px by 12px of padding, a full pill radius, a 0.5px amber border, the soft amber fill and 12.5px bold type. Each reads
   the exact legacy roles, and the declaration order is the order of the source. The caller owns the glyph, the label, the handler and the row.
   No look draws a transparent target (the `blip-` hit rule removes it): the pills are about 30px tall and sit 6px to 8px apart beside a
   neighbor, and the chips wrap in a row with a 7px gap, so a 44px region would overlap its neighbor. */
const PILL_PADDING = '8px 16px', BULK_PADDING = '3px 9px', CHIP_PADDING = '7px 12px', CHIP_MIN_HEIGHT = 32, CHIP_GAP = 6, BULK_FONT = 11.5, CHIP_FONT = 12.5;

export function wedgeBlipPlanStyle(look: WedgeBlipPlanLook): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'blip-plan-chip') return {
    display: 'inline-flex', alignItems: 'center', gap: CHIP_GAP, padding: CHIP_PADDING, minHeight: CHIP_MIN_HEIGHT, borderRadius: radius.pill,
    border: `0.5px solid ${r('amber')}`, background: r('amberSoft'), color: r('amber'), fontSize: CHIP_FONT, fontWeight: weight.bold, cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  };
  const filled = look === 'blip-plan-primary' || look === 'blip-plan-danger';
  return {
    padding: look === 'blip-plan-bulk' ? BULK_PADDING : PILL_PADDING, borderRadius: radius.md,
    border: filled ? 'none' : `0.5px solid ${r('separator')}`,
    background: filled ? r(look === 'blip-plan-danger' ? 'red' : 'accent') : 'transparent',
    color: filled ? r('onAccent') : r('label'), fontSize: look === 'blip-plan-bulk' ? BULK_FONT : font.footnote, fontWeight: weight.bold, cursor: 'pointer',
  };
}
